import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * True when the build environment provided both Supabase env vars.
 * Other modules can read this to render a "configuration missing" banner
 * or to skip auth-dependent UI gracefully.
 *
 * Why we don't throw at module load: a misconfigured deployment used to
 * white-screen the entire app -- including the public `/` welcome page,
 * which doesn't actually need Supabase. The throw was a footgun.
 */
export const isSupabaseConfigured: boolean = Boolean(supabaseUrl && supabaseAnonKey);

if (!isSupabaseConfigured && typeof console !== 'undefined') {
  // Dev-time visibility; production builds with no env vars still log this
  // once to the browser console so the operator can see what's wrong without
  // breaking the visual tree.
  console.error(
    '[CMP] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is missing from the ' +
      'build environment. Auth-dependent features will not work until these ' +
      'are set. The marketing/welcome page will still render.',
  );
}

// Use harmless localhost fallbacks when not configured so createClient does
// not throw on URL validation. Any real auth call will fail fast at the
// network layer with a clear "fetch failed" error that normaliseAuthError()
// surfaces to the user as "Unable to connect."
export const supabase = createClient(
  supabaseUrl || 'http://localhost:54321',
  supabaseAnonKey || 'anon-key-placeholder',
  {
    auth: {
      storage: localStorage,
      persistSession: isSupabaseConfigured,
      autoRefreshToken: isSupabaseConfigured,
    },
  },
);
